import IPosition from "@/models/dto/position.dto"
import IDeviceStore from "@/models/modelStore/IDeviceStore"
import IReportDetails from "@/models/modelView/report-details"
import router from "@/router"
import FlagFeature from "@/services/maps/features/reports/flag.feature"
import VectorService from "@/services/vector.service"
import reportsModule from "@/store/modules/reports.module"
import DateUtil from "@/utils/date.util"
import { Feature } from "ol"
import { LineString, Point } from "ol/geom"
import { fromLonLat } from "ol/proj"
import Icon from "ol/style/Icon"
import Stroke from "ol/style/Stroke"
import Style from "ol/style/Style"
import ModemStrategyFactory from "../modem.strategy/modem.strategy.factory"

export default abstract class ReportStrategy {
    data: IPosition[]
    device?: IDeviceStore
    report: { showGuides: boolean, type: string, device: { id: number, icon: string, plate: string } }

    constructor(report: any, data: any, device: IDeviceStore) {
        this.device = device
        this.report = report
        this.report.device.plate = this.device?.name || 'NaN'
        this.data = data
    }

    async process() {
        this.cleanVector()
        VectorService.insertFeatures(4, [this.getBaseFeature(), this.getStartFeature(), this.getEndFeature(), ...await this.getExtraFeatures()])
        VectorService.centerDataByLayer(4, this.data)
        try {
            reportsModule.setReportDetails(await this.buildReportDetails())
        } catch (error) {
            reportsModule.setShowDetails(false)
        }
        router.push('/maps')
    }

    cleanVector() {
        VectorService.insertStyles(4, [])
        VectorService.clearVectorSource(4)
        VectorService.setViewLayer(5, false)
    }

    abstract getExtraFeatures(): Promise<Feature[]>

    getBaseFeature() {
        const feature = this.getLineFeature()
        this.report.showGuides
            ? VectorService.insertStyles(4, this.getGuides(feature).concat([this.getLineStyle()]))
            : feature.setStyle(this.getLineStyle())
        return feature
    }

    getLineFeature() {
        const line = this.data.map(position => {
            return fromLonLat([position.longitude, position.latitude])
        })
        const reportFeature = new Feature({
            geometry: new LineString(line), //recoge las coordenadas de ubicacion
            type: 'report'
        })
        return reportFeature
    }

    getLineStyle() {
        return new Style({
            stroke: new Stroke({
                color: "#1A237E",
                lineJoin: 'round',
                width: 6
            })
        })
    }

    getGuides(feature: Feature<LineString>) {
        const geometry = feature.getGeometry()!
        const styles: Style[] = []
        geometry.forEachSegment(function (start, end) {
            const dx = end[0] - start[0]
            const dy = end[1] - start[1]
            const promedio = []
            promedio.push((start[0] + end[0]) / 2)
            promedio.push((start[1] + end[1]) / 2)
            const rotation = Math.atan2(dy, dx)
            if ((end[0] != start[0]) && (end[1] != start[1])) {
                styles.push(
                    new Style({
                        geometry: new Point(promedio),
                        image: new Icon({
                            src: require("@/assets/img/arrow_blue.png"),
                            scale: 0.6,
                            anchor: [0.75, 0.5],
                            rotateWithView: true,
                            rotation: -rotation,
                        })
                    })
                )
            }
        })
        return styles
    }

    getStartFeature() {
        return new FlagFeature({
            date: DateUtil.getGenericFormat(this.data[0].deviceTime),
            latitude: this.data[0].latitude,
            longitude: this.data[0].longitude,
            icon: 'pointer_start_report',
            title: 'INICIO DEL RECORRIDO',
            report: this.report,
            type: 'start'
        }).create()
    }

    getEndFeature() {
        return new FlagFeature({
            date: DateUtil.getGenericFormat(this.data[this.data.length - 1].deviceTime),
            latitude: this.data[this.data.length - 1].latitude,
            longitude: this.data[this.data.length - 1].longitude,
            icon: 'pointer_end_report',
            title: 'FIN DE RECORRIDO',
            report: this.report,
            type: 'end'
        }).create()
    }

    async buildReportDetails(): Promise<IReportDetails> {
        const report = ModemStrategyFactory.create(this.device!.model).getReportDetails(this.data, this.device!)

        if (report) {
            return report
        } else {
            return {
                columns: [],
                icon: 'NaN',
                plate: 'NaN',
                visible: false
            }
        }

    }
}